import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AUTH_TOKEN } from "constants/AuthConstant";
import FirebaseService from "services/FirebaseService";
import AuthService from "services/AuthService";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import momentTimezone from "moment-timezone";
import { setCurrentProject } from "./projectSlice"; // project 슬라이스의 액션 import

import Utils from "utils";

// Assuming the URL is http://example.com/firstParam/secondParam
const currentUrl = window.location.href; // gets the full URL
const url = new URL(currentUrl);
const pathSegments = url.pathname.split("/"); // splits the path by "/"

// Getting the first parameter from the path
// The first element in the array is an empty string before the first "/", so the second element is the first parameter
const firstParam = pathSegments[1];

export const initialState = {
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  // token: localStorage.getItem(AUTH_TOKEN) || null,
  token:
    localStorage.getItem("companyId") === firstParam
      ? localStorage.getItem(AUTH_TOKEN)
      : null,
  menu: [],
};

export const signIn = createAsyncThunk(
  "auth/signIn",
  async (data, { rejectWithValue, dispatch }) => {
    const { email, password, company_id } = data;
    try {
      // SAML SSO: returnUrl을 localStorage에 저장 (로그인 전)
      const urlParams = new URLSearchParams(window.location.search);
      const returnUrl = urlParams.get('returnUrl');
      if (returnUrl) {
        localStorage.setItem('saml_return_url', returnUrl);
        console.log('[SAML Redux] Saved returnUrl:', returnUrl);
      }

      localStorage.setItem("companyId", company_id);
      const response = await AuthService.login({
        username: email,
        password,
        ncpid: company_id,
      });
      if (response.status) {
        const token = response.token;
        localStorage.setItem(AUTH_TOKEN, token);
        return { token: token, response: response };
      } else if (response.code === -7) {
        // 2FA 인증 필요
        localStorage.setItem('twofa_user_id', response.id);
        localStorage.setItem('twofa_company_id', company_id);
        localStorage.setItem('twofa_email', email);
        localStorage.setItem('twofa_phone_registered', response.twofa?.phone_registered ? '1' : '0');
        localStorage.setItem('twofa_email_registered', response.twofa?.email_registered ? '1' : '0');
        return { twofa_required: true, id: response.id, company_id };
      } else {
        localStorage.removeItem(AUTH_TOKEN);
        // 로그인 실패 시 project.currentProject 초기화
        dispatch(setCurrentProject(null));
        return rejectWithValue(response.error || "Error");
      }
    } catch (error) {
      localStorage.removeItem(AUTH_TOKEN);
      // 로그인 예외 발생 시 project.currentProject 초기화
      dispatch(setCurrentProject(null));
      return rejectWithValue(error.message || "Error");
    }
  }
);

export const signUp = createAsyncThunk(
  "auth/signUp",
  async (data, { rejectWithValue }) => {
    const { email, password, company_id, name } = data;
    localStorage.setItem("companyId", company_id);
    try {
      const response = await AuthService.register({
        username: email,
        ncp_project_id: company_id,
        password,
        name,
        timezone: momentTimezone.tz.guess(),
      });
      if (response.user) {
        // Sign-in logic after successful sign-up
        const signInResponse = await AuthService.login({
          username: email,
          password,
          ncpid: company_id,
        });

        if (signInResponse.status) {
          const token = signInResponse.token;
          localStorage.setItem(AUTH_TOKEN, token);
          window.location.href = `/${company_id}/projects`;
          return { token: token, response: signInResponse };
        } else {
          localStorage.removeItem("companyId");
          return rejectWithValue(
            signInResponse.error || "Error during sign-in"
          );
        }
      } else {
        localStorage.removeItem("companyId");
        return rejectWithValue(response.error || "Error during sign-up");
      }
    } catch (err) {
      localStorage.removeItem("companyId");
      return rejectWithValue(Utils.getErrorMessages(err));
    }
  }
);

export const signOut = createAsyncThunk("auth/signOut", async (_, { dispatch }) => {
  const response = await AuthService.logout();
  // project.currentProject 초기화
  dispatch(setCurrentProject(null));
  return response.data;
});

export const signInWithGoogle = createAsyncThunk(
  "auth/signInWithGoogle",
  async (_, { rejectWithValue }) => {
    const response = await FirebaseService.signInGoogleRequest();
    if (response.user) {
      const token = response.user.refreshToken;
      localStorage.setItem(AUTH_TOKEN, response.user.refreshToken);
      return token;
    } else {
      return rejectWithValue(response.message?.replace("Firebase: ", ""));
    }
  }
);

export const signInWithFacebook = createAsyncThunk(
  "auth/signInWithFacebook",
  async (_, { rejectWithValue }) => {
    const response = await FirebaseService.signInFacebookRequest();
    if (response.user) {
      const token = response.user.refreshToken;
      localStorage.setItem(AUTH_TOKEN, response.user.refreshToken);
      return token;
    } else {
      return rejectWithValue(response.message?.replace("Firebase: ", ""));
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      console.log("authenticated");
      state.loading = false;
      state.redirect = "";
      state.token = action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    clearRedirect: (state) => {
      state.redirect = "";
    },
    signOutSuccess: (state, action) => {
      state.loading = false;
      state.token = null;
      state.menu = []; // 메뉴 상태 초기화
      
      // URL에서 companyId 추출
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      const urlCompanyId = pathSegments.length >= 1 && pathSegments[0] !== 'auth' ? pathSegments[0] : '';
      const companyId = urlCompanyId || localStorage.getItem("companyId");
      
      state.redirect = companyId ? `/${companyId}/auth/login` : `/auth/login`;
      
      // 중요: 여기서는 직접 다른 슬라이스의 상태를 수정할 수 없으므로 
      // 이 액션을 디스패치하는 곳에서 setCurrentProject(null)도 함께 디스패치해야 함
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      console.log("signInSuccess");
      state.loading = false;
      state.token = action.payload;
    },
    setMenu: (state, action) => {
      state.menu = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;

        // 2FA 인증 필요 처리
        if (action.payload.twofa_required) {
          state.redirect = `/${action.payload.company_id}/auth/verify-2fa`;
          return;
        }

        state.token = action.payload.token;

        // 메뉴 상태 초기화 - 로그인 시 메뉴가 접힌 상태로 시작하도록 함
        localStorage.removeItem('menu_open_keys');
        localStorage.setItem('menu_open_keys', JSON.stringify([]));

        // 중요: API 응답에서 받은 ncpid와 project_id를 저장
        const ncpid = action.payload.response.ncpid;
        const projectId = action.payload.response.project_id;

        // localStorage에 저장
        localStorage.setItem("companyId", ncpid);
        localStorage.setItem("projectId", projectId);

        // SAML SSO 체크: returnUrl이 있으면 바로 리다이렉트
        const samlReturnUrl = localStorage.getItem('saml_return_url');

        if (samlReturnUrl) {
          console.log('[SAML] Login successful, redirecting to:', samlReturnUrl);
          localStorage.removeItem('saml_return_url');

          // JWT 토큰 추가
          const separator = samlReturnUrl.includes('?') ? '&' : '?';
          const fullUrl = `${samlReturnUrl}${separator}token=${state.token}`;

          setTimeout(() => {
            window.location.href = fullUrl;
          }, 100);
          return;
        }

        // 일반 로그인: 페이지 이동
        const redirectPath = `/${ncpid}/${projectId}/dashboards/default`;
        localStorage.setItem('login_redirect_path', redirectPath);

        // 안전한 페이지 이동을 위해 setTimeout 사용
        setTimeout(() => {
          window.location.href = redirectPath;
        }, 100);
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
        
        // URL에서 companyId 추출
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const urlCompanyId = pathSegments.length >= 1 && pathSegments[0] !== 'auth' ? pathSegments[0] : '';
        const companyId = urlCompanyId || localStorage.getItem("companyId");
        
        // menu 상태 초기화
        state.menu = [];
        
        if (companyId) {
          state.redirect = `/${companyId}/auth/login`;
        } else {
          state.redirect = `/auth/login`;
        }
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.menu = []; // 메뉴 상태 초기화
        
        // URL에서 companyId 추출
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const urlCompanyId = pathSegments.length >= 1 && pathSegments[0] !== 'auth' ? pathSegments[0] : '';
        const companyId = urlCompanyId || localStorage.getItem("companyId");
        
        // 로그인 페이지로 리다이렉션
        const redirectPath = companyId ? `/${companyId}/auth/login` : `/auth/login`;
        state.redirect = redirectPath;
        
        // 메뉴 상태 초기화 - 로그아웃 성공 시 메뉴 상태 제거
        localStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem('menu_open_keys');
        
        // 안전한 페이지 이동을 위해 setTimeout 사용
        setTimeout(() => {
          // project ID 제거
          localStorage.removeItem("projectId");
          window.location.href = redirectPath;
        }, 100);
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.menu = []; // 메뉴 상태 초기화
        
        // URL에서 companyId 추출
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const urlCompanyId = pathSegments.length >= 1 && pathSegments[0] !== 'auth' ? pathSegments[0] : '';
        const companyId = urlCompanyId || localStorage.getItem("companyId");
        
        // 로그인 페이지로 리다이렉션
        const redirectPath = companyId ? `/${companyId}/auth/login` : `/auth/login`;
        state.redirect = redirectPath;
        
        // 메뉴 상태 초기화 - 로그아웃 실패 시에도 메뉴 상태 제거
        localStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem('menu_open_keys');
        
        // 안전한 페이지 이동을 위해 setTimeout 사용
        setTimeout(() => {
          // project ID 제거
          localStorage.removeItem("projectId");
          window.location.href = redirectPath;
        }, 100);
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        // state.token = action.payload;
      })
      .addCase(signUp.rejected, (state, action) => {
        console.log(action);
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signInWithFacebook.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithFacebook.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithFacebook.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      });
  },
});

const authPersistConfig = {
  key: "auth", // key for localStorage key, you might want to namespace it like 'myAppAuth'
  storage,
  whitelist: ["menu"], // You can add other state parts here if needed
};

export const persistedAuthReducer = persistReducer(
  authPersistConfig,
  authSlice.reducer
);

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  clearRedirect,
  signOutSuccess,
  showLoading,
  signInSuccess,
  setMenu,
} = authSlice.actions;

// export default authSlice.reducer;
export default persistedAuthReducer;
